/**
 * L'état et la mémoire d'une section rabattable — MV-UX-01, décision F-11.
 *
 * Module PUR : ni React, ni DOM. `SectionRabattable.tsx` ne fait que le
 * câbler. C'est ce qui rend le comportement testable dans une console qui n'a
 * que des tests de logique (aucune bibliothèque de test de composant).
 *
 * ─── Trois décisions, et leur raison ────────────────────────────────────────
 *
 * 1. **La mémoire est par utilisateur ET par clé.** Deux tuteurs partagent
 *    parfois un poste d'agence : ce que l'un a replié ne doit pas l'être pour
 *    l'autre. Sans utilisateur ou sans clé, rien n'est lu ni écrit.
 *
 * 2. **Une entrée de stockage par rabat**, pas un objet JSON commun. Deux
 *    onglets qui replient deux sections différentes n'écrasent pas le réglage
 *    l'un de l'autre, et une entrée illisible ne fait perdre qu'elle-même.
 *
 * 3. **Seul un geste de l'utilisateur écrit.** L'état par défaut n'est jamais
 *    enregistré : un écran dont le défaut change (MV-UX-02, table de charge)
 *    change pour tous ceux qui n'ont rien choisi.
 *
 * Même contrat de stockage que `theme/apparence-console.ts` : quand le
 * stockage fonctionne il fait autorité ; quand il est refusé (navigation
 * privée, témoins bloqués, quota) un repli en mémoire tient l'état le temps de
 * la page. Dégradation, pas panne — et aucune fonction d'ici ne lève.
 */

/** Préfixe des clés. Versionné, comme `silex.console.apparence.v1`. */
export const PREFIXE_CLE_RABAT = 'silex.console.rabat.v1';

/** Ce dont la mémoire a besoin d'un stockage. `Storage` le satisfait. */
export interface StockageRabat {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
  removeItem(cle: string): void;
}

const OUVERTE = '1';
const REPLIEE = '0';

/**
 * La clé de stockage d'un rabat, ou `null` quand il ne doit pas être mémorisé
 * (pas de `cleMemoire`, ou pas d'utilisateur connu).
 */
export function cleDeStockage(
  utilisateurId: string | null | undefined,
  cleMemoire: string | null | undefined,
): string | null {
  const utilisateur = utilisateurId?.trim();
  const cle = cleMemoire?.trim();
  if (!utilisateur || !cle) return null;
  return `${PREFIXE_CLE_RABAT}.${utilisateur}.${cle}`;
}

/** `localStorage` s'il est réellement utilisable, `null` sinon. Ne lève jamais. */
export function stockageNavigateur(): StockageRabat | null {
  try {
    const s = globalThis.localStorage;
    if (!s) return null;
    const sonde = `${PREFIXE_CLE_RABAT}.sonde`;
    s.setItem(sonde, '1');
    s.removeItem(sonde);
    return s;
  } catch {
    return null;
  }
}

/** Repli en mémoire, pour les seuls navigateurs qui refusent le stockage. */
const memoire = new Map<string, boolean>();

/** Vide le repli en mémoire. Pour les tests. */
export function oublierMemoireRabats(): void {
  memoire.clear();
}

/**
 * L'état mémorisé d'un rabat : `true` ouvert, `false` replié, `null` si
 * l'utilisateur n'a jamais rien choisi (ou si la valeur stockée est illisible).
 */
export function lireEtatRabat(
  cle: string | null,
  stockage: StockageRabat | null = stockageNavigateur(),
): boolean | null {
  if (cle === null) return null;
  if (stockage) {
    try {
      const lu = stockage.getItem(cle);
      if (lu === OUVERTE) return true;
      if (lu === REPLIEE) return false;
      return null;
    } catch {
      // Stockage refusé en cours de route : on retombe sur la mémoire.
    }
  }
  return memoire.get(cle) ?? null;
}

/** Enregistre le choix de l'utilisateur. Sans clé, ne fait rien. */
export function ecrireEtatRabat(
  cle: string | null,
  ouverte: boolean,
  stockage: StockageRabat | null = stockageNavigateur(),
): void {
  if (cle === null) return;
  memoire.set(cle, ouverte);
  try {
    stockage?.setItem(cle, ouverte ? OUVERTE : REPLIEE);
  } catch {
    // Quota ou stockage refusé : la mémoire ci-dessus tient l'état.
  }
}

/** L'état d'arrivée : le choix mémorisé s'il existe, sinon le défaut de l'écran. */
export function etatInitial(ouverteParDefaut: boolean, memorise: boolean | null): boolean {
  return memorise ?? ouverteParDefaut;
}

/**
 * L'état affiché. En mode contrôlé (`ouverteControlee` définie), le parent
 * fait autorité et l'état interne est ignoré.
 */
export function etatAffiche(ouverteControlee: boolean | undefined, interne: boolean): boolean {
  return ouverteControlee ?? interne;
}

/**
 * La ligne affichée sous le titre, dans le bouton d'en-tête.
 *
 * - Repliée : le `resume` — c'est lui qui allège sans cacher (« 3 acquises ·
 *   2 à consolider »). Sans résumé, RIEN : le sous-titre d'une section ouverte
 *   (une légende, une date) ne dit rien d'un contenu qu'on ne voit pas.
 * - Ouverte : le `sous`. Le résumé disparaît — il répéterait le contenu.
 *
 * `null`, `undefined`, `false` et la chaîne vide valent « pas de ligne » : un
 * appelant écrit `resume={n > 0 && …}` sans laisser une ligne vide à l'écran.
 */
export function ligneDeTete<T>(
  ouverte: boolean,
  lignes: { resume?: T; sous?: T },
): { readonly nature: 'resume' | 'sous'; readonly contenu: T } | null {
  const contenu = ouverte ? lignes.sous : lignes.resume;
  if (contenu === undefined || contenu === null || contenu === false || contenu === '') return null;
  return { nature: ouverte ? 'sous' : 'resume', contenu };
}

/** Les identifiants liés d'un rabat, dérivés d'une base unique dans la page. */
export interface IdsRabat {
  readonly bouton: string;
  readonly libelle: string;
  readonly ligne: string;
  readonly corps: string;
}

export function idsRabat(base: string): IdsRabat {
  return {
    bouton: `${base}-bouton`,
    libelle: `${base}-libelle`,
    ligne: `${base}-ligne`,
    corps: `${base}-corps`,
  };
}

/** Ce que le bouton d'en-tête annonce. */
export interface AttributsBoutonRabat {
  readonly id: string;
  readonly 'aria-expanded': boolean;
  readonly 'aria-controls': string;
  readonly 'aria-labelledby': string;
  readonly 'aria-describedby': string | undefined;
}

/** Ce que porte le contenu. */
export interface AttributsCorpsRabat {
  readonly id: string;
  readonly role: 'region' | 'group';
  readonly 'aria-labelledby': string;
  readonly hidden: boolean;
}

/**
 * Les attributs d'accessibilité du bouton et du contenu, pour un état donné.
 *
 * - Le NOM du bouton est le titre seul, stable : il ne change pas quand la
 *   section se replie. La ligne sous le titre (résumé ou sous-titre) est sa
 *   DESCRIPTION, et seulement quand elle est à l'écran — un `aria-describedby`
 *   vers un élément absent est muet.
 * - `hidden` retire le contenu replié de l'ordre de tabulation et de l'arbre
 *   d'accessibilité d'un seul geste.
 * - Une section est un repère (`region`) ; une tuile ne l'est pas (`group`) :
 *   un tableau de bord de huit tuiles ferait huit repères, et une liste de
 *   repères trop longue ne sert plus à naviguer.
 */
export function attributsRabat(
  ids: IdsRabat,
  etat: { ouverte: boolean; ligneAffichee: boolean; variante: 'section' | 'tuile' },
): { bouton: AttributsBoutonRabat; corps: AttributsCorpsRabat } {
  return {
    bouton: {
      id: ids.bouton,
      'aria-expanded': etat.ouverte,
      'aria-controls': ids.corps,
      'aria-labelledby': ids.libelle,
      'aria-describedby': etat.ligneAffichee ? ids.ligne : undefined,
    },
    corps: {
      id: ids.corps,
      role: etat.variante === 'section' ? 'region' : 'group',
      'aria-labelledby': ids.libelle,
      hidden: !etat.ouverte,
    },
  };
}
